import React, { type FC, useMemo, useState } from 'react'
import { isPending, isRejected, isResolved, usePromise } from 'react-sync-promise'
import { FormattedNumber } from 'react-intl'
import { Box, type SxProps, type Theme } from '@mui/material'

import type { ReadonlyDate, ReadonlyRecord } from '../../../../utils'

import { type ConnectwareError, type Metrics as MetricsModel, selectLicenseId, Translation } from '../../../../domain'

import { Table, type TableProps, ToggleButton, ToggleButtonGroup } from '../../common'
import { FormattedDateTime, useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'
import { useRetryKey } from '../../utils'

const dateSort = (t: ReadonlyDate): number => t.getTime()

const wrapperStyle: SxProps<Theme> = { py: 4 }
const toggleWrapperStyle: SxProps<Theme> = { mb: 3 }

type MetricsType = 'hourly' | 'daily'

const commonTableProps: Pick<TableProps<ReadonlyRecord<string, unknown>>, 'pagination' | 'translations' | 'search'> = {
    pagination: { pageSize: 10, pageSizeOptions: [10, 25, 50, 100] as const },
    translations: { noResultsOrEmptyTable: Translation.NO_METRICS },
    search: true,
}

export const Metrics: FC = () => {
    const [metricsType, setMetricsType] = useState<MetricsType>('hourly')
    const translator = useTranslator()
    const systemMetricsUsecase = useAppUsecase('systemMetricsUsecase')

    const licenseID = useAppState(selectLicenseId)

    /** Retrying fetches the metrics again, as a new promise */
    const [fetchAttempt, retryFetch] = useRetryKey()
    const metrics = usePromise<MetricsModel, ConnectwareError>(useMemo(() => systemMetricsUsecase.fetchMetrics(), [systemMetricsUsecase, fetchAttempt]))

    const loaded = isResolved(metrics) ? metrics.value : null
    const loading = isPending(metrics)
    const error = isRejected(metrics) ? { error: metrics.value, onRetry: retryFetch } : undefined
    const hourly = loaded?.hourly ?? []
    const daily = loaded?.daily ?? []

    return (
        <Box sx={wrapperStyle}>
            <Box sx={toggleWrapperStyle}>
                <ToggleButtonGroup
                    data-testid="metrics-toggle"
                    value={metricsType}
                    exclusive
                    /**
                     * When we click the same button again, MUI returns null (deselects it).
                     * We only accept a valid type, ignoring null to keep a selection active.
                     */
                    onChange={(_, newType: MetricsType | null) => newType && setMetricsType(newType)}
                >
                    <ToggleButton value="hourly" aria-label={translator.formatTranslation(Translation.MESSAGE_PER_HOUR)} data-testid="hourly-toggle">
                        {translator.formatTranslation(Translation.MESSAGE_PER_HOUR)}
                    </ToggleButton>
                    <ToggleButton value="daily" aria-label={translator.formatTranslation(Translation.MESSAGE_PER_DAY)} data-testid="daily-toggle">
                        {translator.formatTranslation(Translation.MESSAGE_PER_DAY)}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {metricsType === 'hourly' && (
                <Table
                    data-testid="metrics-per-hour"
                    columns={{
                        timestamp: {
                            label: Translation.TIME,
                            customCellRender: (date) => <FormattedDateTime date={date} format="datetime" />,
                            sort: dateSort,
                            type: 'datetime',
                        },
                        numberOfConnectedDatapoints: { label: Translation.NUMBER_OF_DATAPOINTS, sort: true, type: 'number' },
                        numberOfReceivedMessages: {
                            label: Translation.NUMBER_OF_MESSAGES_PER_HOUR,
                            customCellRender: (value) => <FormattedNumber value={value} style="decimal" />,
                            sort: true,
                            type: 'number',
                        },
                    }}
                    sortOrder={{ name: 'timestamp', direction: 'desc' }}
                    data={hourly}
                    loading={loading}
                    error={error}
                    onDownloadCSV={loaded ? () => systemMetricsUsecase.downloadHourlyMetricCSV(hourly) : undefined}
                    {...commonTableProps}
                />
            )}

            {metricsType === 'daily' && (
                <Table
                    data-testid="metrics-per-day"
                    columns={{
                        date: {
                            label: Translation.DATE,
                            customCellRender: (date) => <FormattedDateTime date={date} format="dateOnly" />,
                            sort: dateSort,
                            type: 'datetime',
                        },
                        numberOfConnectedDatapoints: { label: Translation.NUMBER_OF_DATAPOINTS, sort: true, type: 'number' },
                        numberOfReceivedMessages: {
                            label: Translation.NUMBER_OF_MESSAGES_PER_DAY,
                            customCellRender: (value) => <FormattedNumber value={value} style="decimal" />,
                            sort: true,
                            type: 'number',
                        },
                    }}
                    sortOrder={{ name: 'date', direction: 'desc' }}
                    data={daily.map((d) => ({ licenseID, ...d }))}
                    loading={loading}
                    error={error}
                    onDownloadCSV={loaded ? () => systemMetricsUsecase.downloadDailyMetricCSV(daily) : undefined}
                    {...commonTableProps}
                />
            )}
        </Box>
    )
}
